import { EmployeeTimeoffPoliciesFromLeavesDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { GraphQLClient } from 'modules/core/lib/graphql/provider'

import type { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import { leaveValidFinishOn } from 'modules/timeoff/lib/leaveEmployees'
import rangeByDay, { rangeByDayWithFrenchCalendar } from 'modules/timeoff/lib/rangeByDay'
import type LeaveModel from 'modules/timeoff/store/models/Leave'

import insertToTree from './insertToTree'

const getRange = (leave: LeaveModel, employeeTimeoffPolicyId: number | null | undefined) => {
  const startDate = moment(leave.attributes.start_on)
  const endDate = leaveValidFinishOn(leave, employeeTimeoffPolicyId)

  // Get allowance information to determine if we need French calendar logic
  const allowance = leave.leaveType?.getAllowanceForEmployee(employeeTimeoffPolicyId)

  // Use French calendar logic if allowance is French calendar days
  if (allowance && (allowance.frenchCalendarDays || allowance.frenchOuvre)) {
    return rangeByDayWithFrenchCalendar(startDate, endDate, allowance)
  }

  // Default behavior for other allowance types
  return rangeByDay(startDate, endDate)
}

class LeavesToDateTree {
  leaves: LeaveModel[]
  memo: DaysTree

  constructor(leaves: LeaveModel[]) {
    this.leaves = leaves
    this.memo = {}
  }

  /**
   * Spreads the range of days for a given leave
   * in a tree shape so it can be searched efficiently.
   *
   * The tree has the shape of:
   *
   *  {
   *    year: {
   *      month: [{date: number, type: string}]
   *    }
   *  }
   */
  async munch(graphql: GraphQLClient): Promise<DaysTree> {
    const { data } = await graphql.query({
      query: EmployeeTimeoffPoliciesFromLeavesDocument,
      variables: {
        leaveIds: this.leaves.map((leave) => leave.attributes.id),
      },
    })

    this.leaves.forEach((leave: LeaveModel) => {
      if (leave.isRejected()) return

      const employeeTimeoffPolicyId = data.timeoff.leaves.find(
        ({ id }) => id === leave.attributes.id
      )?.employee?.timeoffPolicy?.id

      getRange(leave, employeeTimeoffPolicyId).forEach((day) => {
        insertToTree(this.memo, '')(day)
      })
    })

    return this.memo
  }
}

export default LeavesToDateTree
